/**
 * Naming a variant, in one place (SCOPE §5.2).
 *
 * "Which one is this?" is asked on a cart line, a receipt, a label, a purchase
 * order, a receiving document and a report, and the answer has to be the same
 * string every time — a customer comparing a receipt against a label is doing
 * a string comparison whether they know it or not.
 *
 * **This existed four times**, byte for byte, in `sale-service.ts`, twice in
 * `purchasing.ts` and in the POS's `order-draft.ts` — one of them already
 * called `describeVariant`. That is the shape this codebase keeps finding
 * (`.chip` twice with different geometry, `TENDER_LABELS` three times with
 * "Cheque" in one and "Check" in another *on the same sale*), and it was found
 * here the moment a fourth field had to be added to all four.
 */

export interface DescribableVariant {
  readonly productName: string | null;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  /**
   * The second size axis, where the product has one — a trouser inseam beside
   * its waist, an orthotic's arch height beside its shoe size, a skate wheel's
   * durometer beside its diameter.
   *
   * Optional so every existing caller keeps compiling, and so a shop that never
   * uses one never sees it.
   */
  readonly altSize?: string | null;
}

/**
 * `PANT · BLACK · 12 · R` — name, colour, then the size axes in order.
 *
 * Empty parts drop out rather than leaving ` ·  · ` behind, so a single-variant
 * product is just its name.
 */
export function describeVariant(variant: DescribableVariant): string {
  return [variant.productName, variant.colorName, variant.sizeLabel, variant.altSize]
    .filter((part) => part != null && part !== '')
    .join(' · ');
}

export interface VariantLines {
  /** The style name, alone. */
  readonly name: string;
  /** `12 · R · BLACK`, or empty on a product with neither size nor colour. */
  readonly detail: string;
}

/**
 * The same facts on two lines, for a document with room for two — today, the
 * printed label (revision item 37).
 *
 * **Size leads, and that is the client's call rather than ours.** A receipt
 * reads `name · colour · size` because a customer reads it as a sentence; a
 * label is read on a rail of six of the same jacket, where size is the only
 * thing being looked for. Both are drawn from these fields and neither
 * reimplements the other, which is what the rule about one document and one
 * rendering is actually protecting.
 *
 * The two size axes stay adjacent — a trouser is a waist *and* an inseam, and
 * splitting them across the colour would read as three unrelated facts.
 */
export function describeVariantLines(variant: DescribableVariant): VariantLines {
  const present = (part: string | null | undefined) => part != null && part !== '';

  return {
    name: present(variant.productName) ? variant.productName! : '',
    detail: [variant.sizeLabel, variant.altSize, variant.colorName].filter(present).join(' · '),
  };
}
